import React from "react";
import BackButtonRow from "../../components/shared/back-button-row";
import { ClientInferResponseBody } from "@ts-rest/core";
import { apiContract } from "tekkr-common/dist/model/api/api.contract";
import {
    BookTextIcon,
    BotIcon,
    BotMessageSquareIcon,
    CalendarIcon,
    CalendarOffIcon,
    CalendarPlusIcon,
    CaptionsIcon,
    FileUpIcon,
    UsersIcon,
} from "lucide-react";
import { DateTime } from "luxon";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { TekkrMarkdown } from "../../components/shared/markdown/tekkr-markdown";
import { Badge } from "../../components/ui/badge";
import Spinner from "../../components/ui/spinner";
import { CopyableTextField } from "../../components/shared/copyable-text-field";
import { Textarea } from "../../components/ui/textarea";
import removeMd from "remove-markdown";
import { useProject } from "../../data/project";
import { useUploadMeetingTranscriptMutation } from "../../data/mutations/upload-meeting-transcript";
import { useScheduledProjectMeetingsQuery } from "../../data/queries/scheduled-project-meetings";
import { ProjectPageHeader } from "../project/components/project-page-header";
import { useCreateMeetingTrackerMutation } from "../../data/mutations/create-meeting-tracker";
import { useOrg } from "../../auth/org-provider";
import { useScheduleEvent } from "../../lib/event-scheduling";
import { useProjectChatUi, useSetProjectChatContext } from "../project/components/project-chat";

type MeetingItem = ClientInferResponseBody<typeof apiContract.getScheduledProjectMeetings, 200>["data"][number];

function MeetingItemView({ meeting }: { meeting: MeetingItem }) {
    const projectChat = useProjectChatUi();

    const followUp = () => {
        projectChat.startChatWithMessage(
            `Let's follow up on the meeting :reference[_${meeting.title}_]{#${meeting.id}}. ` +
                `Should I change the project plan somehow? Please suggest any necessary updates based on the meeting.`
        );
    };

    return (
        <div className={"flex flex-col gap-2 rounded-md border p-3"}>
            <div className={"flex flex-row items-center gap-4"}>
                <StatusBadge meeting={meeting} />
                <div className={"flex flex-row items-center gap-1.5 text-sm font-medium text-muted-foreground"}>
                    <CalendarIcon className={"h-4 w-4"} />
                    {DateTime.fromISO(meeting.start).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}
                </div>
                <div className={"flex flex-row items-center gap-1.5 text-sm font-medium text-muted-foreground"}>
                    <UsersIcon className={"h-4 w-4"} />
                    {meeting.inviteeCount}
                </div>
            </div>
            <h4>{meeting.title}</h4>
            {meeting.description && (
                <p className={"line-clamp-2 text-sm text-muted-foreground"}>{removeMd(meeting.description)}</p>
            )}

            {isMeetingMissed(meeting) && (
                <UploadTranscriptDialog meeting={meeting}>
                    <DialogTrigger className={"self-start"}>
                        <Button size={"sm2"} variant={"secondary"}>
                            <FileUpIcon className={"me-2 h-4 w-4"} />
                            Upload transcript
                        </Button>
                    </DialogTrigger>
                </UploadTranscriptDialog>
            )}

            {isMeetingComplete(meeting) && (
                <div className={"mt-1 flex flex-row items-center gap-2"}>
                    <Button size={"sm"} onClick={followUp}>
                        <BotMessageSquareIcon className={"me-2 h-5 w-5"} />
                        Follow up
                    </Button>
                    {meeting.summary && (
                        <MeetingArtifactDialog summary={meeting.summary}>
                            <DialogTrigger>
                                <Button variant={"outline-primary"} size={"sm"}>
                                    <BookTextIcon className={"me-2 h-5 w-5"} />
                                    Show summary
                                </Button>
                            </DialogTrigger>
                        </MeetingArtifactDialog>
                    )}
                    <MeetingArtifactDialog transcript={meeting.transcript!}>
                        <DialogTrigger>
                            <Button variant={"outline"} size={"sm"}>
                                <CaptionsIcon className={"me-2 h-5 w-5"} />
                                Show transcript
                            </Button>
                        </DialogTrigger>
                    </MeetingArtifactDialog>
                </div>
            )}
        </div>
    );
}

function UploadTranscriptDialog(props: React.PropsWithChildren<{ meeting: MeetingItem }>) {
    const [transcript, setTranscript] = React.useState<string | null>(null);
    const uploadMutation = useUploadMeetingTranscriptMutation(props.meeting.id);
    return (
        <Dialog open={uploadMutation.isPending ? true : undefined}>
            {props.children}
            <DialogContent className={"w-[85%] max-w-4xl"}>
                <DialogHeader>
                    <DialogTitle>Upload Transcript</DialogTitle>
                    <p className={"pt-1 text-sm font-medium"}>Meeting: {props.meeting.title}</p>
                </DialogHeader>
                <Textarea
                    onChange={(e) => setTranscript(e.target.value)}
                    disabled={uploadMutation.isPending}
                    className={"h-[40vh] max-h-[75vh] min-h-[160px]"}
                    placeholder={"Paste your transcript here"}
                />
                <DialogFooter>
                    {uploadMutation.isPending && (
                        <div
                            className={
                                "me-4 flex flex-row items-center gap-2 text-sm text-muted-foreground animate-in slide-in-from-right-4"
                            }
                        >
                            <Spinner className={"h-5 w-5"} />
                            Uploading transcript...
                        </div>
                    )}
                    <DialogClose>
                        <Button disabled={uploadMutation.isPending} variant={"secondary"} onClick={() => {}}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={uploadMutation.isPending || !transcript?.trim()}
                        onClick={() => uploadMutation.mutate(transcript!)}
                    >
                        Upload
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MeetingArtifactDialog(props: React.PropsWithChildren<{ transcript: string } | { summary: string }>) {
    return (
        <Dialog>
            {props.children}
            <DialogContent className={"w-full max-w-3xl"}>
                <DialogTitle>Meeting {"transcript" in props ? "Transcript" : "Summary"}</DialogTitle>
                <div className={"h-full max-h-[80vh] overflow-scroll"}>
                    <TekkrMarkdown markdown={"transcript" in props ? props.transcript : props.summary} />
                </div>
            </DialogContent>
        </Dialog>
    );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ProjectMeetingList({ meetings }: { meetings: MeetingItem[] }) {
    if (!meetings.length) {
        return (
            <div
                className={
                    "flex flex-row items-center justify-center rounded-md border p-6 text-center text-sm text-muted-foreground"
                }
            >
                <CalendarOffIcon className={"me-2 h-4 w-4"} />
                No meetings scheduled yet for this project
            </div>
        );
    }
    return (
        <div className={"flex w-full flex-col gap-4"}>
            {meetings.map((m) => {
                return <MeetingItemView key={m.id} meeting={m} />;
            })}
        </div>
    );
}

export function ProjectMeetingsPage() {
    const meetings = useScheduledProjectMeetingsQuery();
    useSetProjectChatContext({ type: "meetings" });

    return (
        <div className={"flex flex-col items-center py-6"}>
            <div className={"flex w-full max-w-[90%] flex-row items-stretch gap-4 lg:gap-8"}>
                <div className={"flex w-full flex-col gap-2 p-1"}>
                    <div className={"flex flex-col gap-3"}>
                        <BackButtonRow />
                        <div className={"mt-2 flex w-full max-w-3xl flex-col gap-3 self-center"}>
                            <ProjectPageHeader preheader={"Meetings"} />
                            <div className={"mt-2 flex flex-row items-center gap-4"}>
                                <ScheduleNewMeetingButton />
                                <InviteBotToExistingMeetingButton />
                            </div>
                            <p className={"text-sm text-muted-foreground"}>
                                You can schedule a new meeting or add Tekkr Bot to existing meeting related to this
                                project for easier follow-ups and centralized tracking of progress.
                            </p>
                            <div className={"mt-6 font-medium text-muted-foreground"}>Scheduled Meetings</div>
                            {!!meetings.data && <ProjectMeetingList meetings={meetings.data} />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ScheduleNewMeetingButton() {
    const org = useOrg();
    const { project } = useProject();
    const scheduleEvent = useScheduleEvent();

    const handleScheduleNewMeeting = async () => {
        await scheduleEvent(org.officeSuiteProvider, {
            eventName: "",
            description: "",
            meetingDuration: { minutes: 30 },
            context: { projectId: project.id, scope: ["manually-scheduled"] },
        });
    };

    return (
        <Button onClick={handleScheduleNewMeeting} size={"sm"}>
            <CalendarPlusIcon className={"me-2 h-5 w-5"} />
            Add new meeting
        </Button>
    );
}

function InviteBotToExistingMeetingButton() {
    const [presentingAddress, setPresentingAddress] = React.useState<string | null>(null);
    const getBotEmailMutation = useCreateMeetingTrackerMutation({
        onSuccess: (data) => {
            setPresentingAddress(data.emailAddress);
        },
    });
    return (
        <div className={"flex flex-row items-center gap-4 text-sm text-muted-foreground"}>
            <Dialog open={!!presentingAddress} onOpenChange={() => setPresentingAddress(null)}>
                <DialogContent>
                    <DialogTitle className={"flex flex-row items-center"}>
                        <BotIcon className={"me-2 h-5 w-5"} />
                        Invite a bot to your meeting
                    </DialogTitle>
                    <p>
                        Invite this email address to your meeting. As soon as we receive the invite, the meeting will
                        show up on this page and we'll make sure a bot joins at the time of your meeting.
                    </p>
                    <CopyableTextField value={presentingAddress ?? ""} />
                    <p className={"text-sm text-muted-foreground"}>
                        Our bots support Google Meet, Microsoft Teams, and Zoom.
                    </p>
                    <DialogFooter>
                        <Button onClick={() => setPresentingAddress(null)} variant={"secondary"}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Button
                disabled={getBotEmailMutation.isPending}
                onClick={() => getBotEmailMutation.mutate({ scope: ["manually-scheduled"] })}
                variant={"outline-primary"}
                size={"sm"}
            >
                {getBotEmailMutation.isPending ? (
                    <Spinner className={"me-2 h-5 w-5"} />
                ) : (
                    <CalendarPlusIcon className={"me-2 h-5 w-5"} />
                )}
                Add existing meeting
            </Button>
        </div>
    );
}

function StatusBadge({ meeting }: { meeting: MeetingItem }) {
    const isPast = DateTime.now() > DateTime.fromISO(meeting.end);

    if (isMeetingComplete(meeting)) {
        return (
            <Badge className={"pointer-events-none border border-confirmation bg-confirmation/10 text-confirmation"}>
                Concluded
            </Badge>
        );
    }
    if (isPast && (meeting.hasBotDeployed || meeting.transcript)) {
        return (
            <Badge className={"pointer-events-none border border-primary bg-primary/10 text-primary"}>
                <Spinner className={"me-1.5 h-3 w-3"} />
                Processing
            </Badge>
        );
    }
    if (isPast) {
        return (
            <Badge className={"pointer-events-none border border-yellow-500 bg-yellow-500/10 text-yellow-500"}>
                Missed
            </Badge>
        );
    }
    return (
        <Badge className={"pointer-events-none"} variant={"secondary"}>
            Upcoming
        </Badge>
    );
}

function isMeetingComplete(meeting: MeetingItem) {
    return !!meeting.transcript && !!meeting.summary;
}

function isMeetingMissed(meeting: MeetingItem) {
    return DateTime.now() > DateTime.fromISO(meeting.end) && !meeting.transcript && !meeting.hasBotDeployed;
}
